/**
 * MST (UTC-7) timezone helpers.
 *
 * The IT helpdesk operates in one timezone — Mountain Standard Time. We pin
 * to UTC-7 (no daylight-saving auto-shift) so a 2 PM appointment is always
 * 2 PM regardless of where the technician's browser is located.
 *
 * Storage convention:
 *   - Backend stores all timestamps as UTC ISO strings.
 *   - The UI converts to/from MST at the rendering boundary using the helpers
 *     below so users always see and enter MST.
 */

const MST_OFFSET_MIN = 7 * 60; // MST is UTC-7

/**
 * Convert a UTC ISO string to a `<input type="datetime-local">`-compatible
 * MST string ("YYYY-MM-DDTHH:mm").
 *
 * @example utcIsoToMstLocal('2026-04-28T21:30:00Z') → '2026-04-28T14:30'
 */
export function utcIsoToMstLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // Subtract 7h from the UTC instant to get the MST clock-time, then format
  // using UTC accessors so we're not double-shifting through the browser TZ.
  const mst = new Date(d.getTime() - MST_OFFSET_MIN * 60_000);
  const yyyy = mst.getUTCFullYear();
  const mm = String(mst.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(mst.getUTCDate()).padStart(2, '0');
  const hh = String(mst.getUTCHours()).padStart(2, '0');
  const mi = String(mst.getUTCMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

/**
 * Convert an MST-local string ("YYYY-MM-DDTHH:mm") back to a UTC ISO string.
 *
 * @example mstLocalToUtcIso('2026-04-28T14:30') → '2026-04-28T21:30:00.000Z'
 */
export function mstLocalToUtcIso(local) {
  if (!local) return '';
  const [datePart, timePart = '00:00'] = local.split('T');
  const [y, mo, d] = datePart.split('-').map(Number);
  const [h, mi] = timePart.split(':').map(Number);
  if ([y, mo, d, h, mi].some((n) => Number.isNaN(n))) return '';
  // Treat the entered values as MST → add 7h to land at UTC.
  const asIfUtcMs = Date.UTC(y, mo - 1, d, h, mi);
  return new Date(asIfUtcMs + MST_OFFSET_MIN * 60_000).toISOString();
}

/**
 * Render a UTC ISO string as a human-readable MST timestamp like
 * "Apr 28, 2026, 2:30 PM MST".
 */
export function formatMst(iso, opts = {}) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Shift to MST clock-time, then format using UTC accessors so the formatter
  // doesn't apply the browser's local offset on top.
  const mst = new Date(d.getTime() - MST_OFFSET_MIN * 60_000);
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: opts.year === false ? undefined : 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${fmt.format(mst)} MST`;
}

/** Same as formatMst() but date-only. */
export function formatMstDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const mst = new Date(d.getTime() - MST_OFFSET_MIN * 60_000);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(mst);
}

export const MST_LABEL = 'MST';

// IANA zone that is UTC-7 year-round (no DST) — matches the fixed MST convention above.
export const MST_IANA = 'America/Phoenix';

/**
 * Make every Date#toLocale*String() call in the app render in MST unless the
 * caller explicitly passes its own timeZone. Called once from index.js so
 * tables, timelines and timesheets all agree regardless of the browser's zone.
 */
export function installGlobalMst() {
  if (Date.prototype.__mstInstalled) return;
  ['toLocaleString', 'toLocaleDateString', 'toLocaleTimeString'].forEach((name) => {
    const orig = Date.prototype[name];
    Date.prototype[name] = function patched(locale, options) {
      return orig.call(this, locale, { timeZone: MST_IANA, ...(options || {}) });
    };
  });
  Date.prototype.__mstInstalled = true;
}
